import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  DocumentTextIcon,
  CloudArrowUpIcon,
  TrashIcon,
  EyeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentIcon
} from '@heroicons/react/24/outline';

const CertificationSettings = () => {
  const [templates, setTemplates] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);

  // Charger les templates depuis localStorage au montage
  useEffect(() => {
    const savedTemplates = localStorage.getItem('certificationTemplates');
    if (savedTemplates) {
      try {
        const parsedTemplates = JSON.parse(savedTemplates);
        setTemplates(parsedTemplates);
        
        // Charger le template actif
        const activeTemplateId = localStorage.getItem('activeCertificationTemplate');
        if (activeTemplateId) {
          const active = parsedTemplates.find(t => t.id === activeTemplateId);
          setActiveTemplate(active);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des templates:', error);
      }
    }
  }, []);

  // Sauvegarder les templates dans localStorage
  const saveTemplates = (newTemplates) => {
    localStorage.setItem('certificationTemplates', JSON.stringify(newTemplates));
    setTemplates(newTemplates);
  };

  // Gestion de l'upload de fichier
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Vérifier que c'est un PDF
    if (file.type !== 'application/pdf') {
      setErrorMessage('Veuillez sélectionner un fichier PDF.');
      setUploadStatus('error');
      return;
    }

    // Vérifier la taille (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setErrorMessage('Le fichier est trop volumineux. Taille maximale: 10MB.');
      setUploadStatus('error');
      return;
    }

    setUploadStatus('uploading');
    setErrorMessage('');

    // Lire le fichier comme base64
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const base64Data = e.target.result;
        const templateData = {
          id: Date.now().toString(),
          name: file.name.replace('.pdf', ''),
          fileName: file.name,
          size: file.size,
          uploadDate: new Date().toISOString(),
          data: base64Data,
          isActive: templates.length === 0 // Premier template devient actif par défaut
        };

        const newTemplates = [...templates, templateData];
        saveTemplates(newTemplates);

        // Si c'est le premier template, le rendre actif
        if (templates.length === 0) {
          setActiveTemplate(templateData);
          localStorage.setItem('activeCertificationTemplate', templateData.id);
        }

        setUploadStatus('success');
        setTimeout(() => setUploadStatus('idle'), 3000);
      } catch (error) {
        console.error('Erreur lors du traitement du fichier:', error);
        setErrorMessage('Erreur lors du traitement du fichier.');
        setUploadStatus('error');
      }
    };

    reader.onerror = () => {
      setErrorMessage('Erreur lors de la lecture du fichier.');
      setUploadStatus('error');
    };

    reader.readAsDataURL(file);
  };

  // Supprimer un template
  const deleteTemplate = (templateId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce template ?')) {
      const newTemplates = templates.filter(t => t.id !== templateId);
      saveTemplates(newTemplates);

      // Si le template actif est supprimé, désactiver
      if (activeTemplate && activeTemplate.id === templateId) {
        setActiveTemplate(null);
        localStorage.removeItem('activeCertificationTemplate');
      }
    }
  };

  // Activer un template
  const activateTemplate = (template) => {
    setActiveTemplate(template);
    localStorage.setItem('activeCertificationTemplate', template.id);

    // Mettre à jour le statut dans la liste
    const newTemplates = templates.map(t => ({
      ...t,
      isActive: t.id === template.id
    }));
    saveTemplates(newTemplates);
  };

  // Prévisualiser un template
  const previewTemplate = (template) => {
    if (template.data) {
      // Créer un blob à partir des données base64
      const byteCharacters = atob(template.data.split(',')[1]);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      // Ouvrir dans un nouvel onglet
      window.open(url, '_blank');
    }
  };

  // Formatage de la taille de fichier
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-xl rounded-xl p-6 border border-slate-200 dark:border-slate-600">
        <div className="flex items-center space-x-3 mb-4">
          <DocumentTextIcon className="h-6 w-6 text-blue-400" />
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-50">Templates de Certification</h2>
        </div>
        <p className="text-slate-600 dark:text-slate-400 mb-6">
          Uploadez vos propres templates PDF pour personnaliser vos certificats. 
          Les données seront automatiquement superposées sur votre template.
        </p>

        {/* Zone d'upload */}
        <div className="border-2 border-dashed border-slate-200 dark:border-slate-600 rounded-xl p-6 text-center">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileUpload}
            className="hidden"
          />
          
          {uploadStatus === 'idle' && (
            <div>
              <CloudArrowUpIcon className="h-12 w-12 text-slate-500 dark:text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-2">
                Uploader un template PDF
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Format A4 Paysage recommandé (297 x 210 mm)<br />
                Taille maximale: 10MB
              </p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-all"
              >
                Choisir un fichier
              </button>
            </div>
          )}

          {uploadStatus === 'uploading' && (
            <div>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
              <p className="text-slate-900 dark:text-slate-50">Upload en cours...</p>
            </div>
          )}

          {uploadStatus === 'success' && (
            <div>
              <CheckCircleIcon className="h-12 w-12 text-green-400 mx-auto mb-4" />
              <p className="text-green-400 font-medium">Template uploadé avec succès!</p>
            </div>
          )}

          {uploadStatus === 'error' && (
            <div>
              <ExclamationTriangleIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <p className="text-red-400 font-medium mb-2">Erreur d'upload</p>
              <p className="text-red-400/80 text-sm mb-4">{errorMessage}</p>
              <button
                onClick={() => {
                  setUploadStatus('idle');
                  setErrorMessage('');
                }}
                className="text-blue-400 hover:text-blue-300 underline"
              >
                Réessayer
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Template actif */}
      {activeTemplate && (
        <div className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-xl rounded-xl p-6 border border-green-500/30">
          <div className="flex items-center space-x-3 mb-4">
            <CheckCircleIcon className="h-6 w-6 text-green-400" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">Template Actif</h3>
          </div>
          
          <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <DocumentIcon className="h-8 w-8 text-green-400" />
              <div>
                <h4 className="text-slate-900 dark:text-slate-50 font-medium">{activeTemplate.name}</h4>
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  {formatFileSize(activeTemplate.size)} • {new Date(activeTemplate.uploadDate).toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
            <button
              onClick={() => previewTemplate(activeTemplate)}
              className="bg-slate-50 dark:bg-slate-800/80 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-900 dark:text-slate-50 px-4 py-2 rounded-lg flex items-center space-x-2 transition-all"
            >
              <EyeIcon className="h-4 w-4" />
              <span>Aperçu</span>
            </button>
          </div>
        </div>
      )}

      {/* Liste des templates */}
      {templates.length > 0 && (
        <div className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-xl rounded-xl p-6 border border-slate-200 dark:border-slate-600">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-4">Tous les Templates</h3>
          
          <div className="space-y-3">
            {templates.map((template) => (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 border ${
                  activeTemplate && activeTemplate.id === template.id
                    ? 'border-green-500/50 bg-green-500/10'
                    : 'border-slate-200 dark:border-slate-600'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <DocumentIcon className={`h-6 w-6 ${
                      activeTemplate && activeTemplate.id === template.id
                        ? 'text-green-400'
                        : 'text-slate-600 dark:text-slate-400'
                    }`} />
                    <div>
                      <h4 className="text-slate-900 dark:text-slate-50 font-medium flex items-center space-x-2">
                        <span>{template.name}</span>
                        {activeTemplate && activeTemplate.id === template.id && (
                          <span className="bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded-full">
                            Actif
                          </span>
                        )}
                      </h4>
                      <p className="text-slate-600 dark:text-slate-400 text-sm">
                        {formatFileSize(template.size)} • {new Date(template.uploadDate).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => previewTemplate(template)}
                      className="bg-slate-50 dark:bg-slate-800/80 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-900 dark:text-slate-50 p-2 rounded-lg transition-all"
                      title="Aperçu"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    
                    {(!activeTemplate || activeTemplate.id !== template.id) && (
                      <button
                        onClick={() => activateTemplate(template)}
                        className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 px-3 py-2 rounded-lg text-sm transition-all"
                      >
                        Activer
                      </button>
                    )}
                    
                    <button
                      onClick={() => deleteTemplate(template.id)}
                      className="bg-red-500/20 hover:bg-red-500/30 text-red-400 p-2 rounded-lg transition-all"
                      title="Supprimer"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
        <h3 className="text-yellow-400 font-semibold mb-3">💡 Conseils pour votre template</h3>
        <ul className="text-yellow-400/80 space-y-2 text-sm">
          <li>• <strong>Format:</strong> Utilisez A4 Paysage (297 x 210 mm) pour un meilleur rendu</li>
          <li>• <strong>Zones de texte:</strong> Laissez des espaces blancs où les données seront ajoutées</li>
          <li>• <strong>Positionnement:</strong> Les données seront superposées automatiquement</li>
          <li>• <strong>Qualité:</strong> Utilisez une résolution élevée (300 DPI minimum)</li>
          <li>• <strong>Couleurs:</strong> Évitez les fonds trop sombres dans les zones de texte</li>
        </ul>
      </div>
    </div>
  );
};

export default CertificationSettings;